import {
  getAllTagTypes,
  createTagType,
  updateTagType,
} from "api/tagTypes";
import { Loader, BorderGradient, TableManager, PopoverContainer, Modal, DataTable } from "processhub-components-lib";
import { useEffect, useMemo, useState } from "react";
import Debounce from "utilities/Debounce";
import classNames from "utilities/ClassNames";
import useApp from "hooks/useApp";
import { Item } from "types/common";
import modalConfig from "config/modal/modalConfig";
import allHooks from "hooks";
import { ActionName } from "types/common";

export default function TagTypeContainer() {
  const { isScaled, userRole } = useApp();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [data, setData] = useState<any>(null);
  const [managerMode, setManagerMode] = useState<null | string>(null);
  const [tagTypesToDelete, setTagTypesToDelete] = useState<{ added: number[] | string[], removed: number[] | string[] }>({ added: [], removed: [] });

  const [tagType, setTagType] = useState<any>({
    action: "",
    value: null,
  });

  const selectAllTagTypesForDeletion = () => {
    if (!data || data.length === 0) return;

    const allIds = data.map(
      (item: Item) => item.id
    );

    setTagTypesToDelete((prev) => {
      return {
        added: allIds,
        removed: [],
      };
    });
  };

  // table
  const ITEMS_PER_PAGE = 300;
  const [currentPage, setCurrentPage] = useState(1);

  const getData = async () => {
    setLoading(true);
    const response: any = await getAllTagTypes();
    if (response.status === 200) {
      // Transform data to ensure consistent field names
      // Backend might send matchValue, but we use valueKey in frontend
      const transformedData = response.data?.map((item: any) => ({
        ...item,
        valueKey: item.valueKey || item.matchValue || "",
        matchKey: item.matchKey || "",
        valueType: item.valueType || "",
        description: item.description || "",
        name: item.name || "",
      }));
      setData(transformedData);
    } else {
      setError(true);
    }
    setLoading(false);
  };

  const refreshData = async () => {
    const response: any = await getAllTagTypes();
    if (response.status === 200) {
      // Transform data to ensure consistent field names
      const transformedData = response.data?.map((item: any) => ({
        ...item,
        valueKey: item.valueKey || item.matchValue || "",
        matchKey: item.matchKey || "",
        valueType: item.valueType || "",
        description: item.description || "",
        name: item.name || "",
      }));
      setData(transformedData);
    }
  };

  useEffect(() => {
    if (tagTypesToDelete && tagTypesToDelete.added.length > 0) {
      setManagerMode('massDeletion');
    } else {
      setManagerMode(null);
    }
  }, [tagTypesToDelete]);

  useEffect(() => {
    if (managerMode === null) {
      setTagTypesToDelete({ added: [], removed: [] });
    }
  }, [managerMode]);

  useEffect(() => {
    getData();
  }, []);

  // Table
  const [keyword, setKeyword] = useState("");
  const [debouncedKeyword, setDebouncedKeyword] = useState("");
  const [filteredData, setfilteredData] = useState<any>(data);

  Debounce(() => setDebouncedKeyword(keyword), [keyword], 200);

  const rows = useMemo(() => {
    let items = data;
    const keyword = debouncedKeyword.toString().toLowerCase();
    if (keyword !== "") {
      items = data?.filter(
        (item: any) =>
          (item.name && item.name.toLowerCase().includes(keyword)) ||
          (item.name && item.name.toLowerCase() === keyword) ||
          (item.description && item.description?.toLowerCase().includes(keyword)) ||
          (item.description && item.description?.toLowerCase() === keyword) ||
          (item.matchKey && item.matchKey?.toLowerCase().includes(keyword)) ||
          (item.valueKey && item.valueKey?.toLowerCase().includes(keyword)) ||
          (item.matchValue && item.matchValue?.toLowerCase().includes(keyword)) ||
          (item.valueType && item.valueType?.toLowerCase().includes(keyword))
      );
    }

    if (items) {
      // Transform data to ensure all fields are accessible
      // Backend might send matchValue, but we use valueKey in frontend
      const transformedItems = items.map((item: any) => ({
        ...item,
        valueKey: item.valueKey || item.matchValue || "",
        matchKey: item.matchKey || "",
        valueType: item.valueType || "",
        description: item.description || "",
        name: item.name || "",
      }));
      setfilteredData(transformedItems);
      return transformedItems?.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        (currentPage - 1) * ITEMS_PER_PAGE + ITEMS_PER_PAGE
      );
    } else return [];
  }, [data, debouncedKeyword, currentPage]);

  const downloadFilterData = () => {
    if (filteredData && filteredData.length > 0) {
      // CSV download logic can be added here if needed
    }
  };

  const handleClick = (itemValue: Item, itemAction: ActionName) => {
    setTagType({
      action: itemAction,
      value: itemValue
    });
  };

  const handleManagerBtnClick = () => {
    setTagType({ action: "new", value: null });
  };

  const filterItems: any[] = [];

  const tagTypeDeletion = () => {
    setTagType({
      action: 'delete',
      value: tagTypesToDelete.added
    });
  };

  return (
    <>
      {loading && (
        <div className="w-full h-[80vh] grid place-content-center">
          <Loader />
        </div>
      )}
      {error && (
        <div className="w-full h-[80vh] grid place-content-center">
          <p>Error retrieving Tag Types, try refreshing the page</p>
        </div>
      )}
      {!loading && !error && data && (
        <div
          className={classNames(
            isScaled ? "text-2xl" : "text-base",
            "pl-3 pb-3 overflow-hidden overflow-y-scroll"
          )}
        >
          <TableManager 
            userRole={userRole} 
            mode={managerMode} 
            secondaryBtnAction={selectAllTagTypesForDeletion} 
            setMode={setManagerMode} 
            type="tag-types" 
            setKeyword={setKeyword} 
            btnAction={handleManagerBtnClick} 
            downloadfiltereddata={downloadFilterData} 
            specialButtonAction={tagTypeDeletion}
          >
            <PopoverContainer items={filterItems} />
          </TableManager>
          <div className="mt-3 h-[78vh]">
            <BorderGradient className=" overflow-hidden h-full">
              <DataTable 
                userRole={userRole} 
                mode={managerMode} 
                type="tagTypes" 
                setMappedTags={setTagTypesToDelete} 
                mappedTags={tagTypesToDelete} 
                hasPins={false} 
                hasActions={true} 
                rows={rows} 
                onClickHandler={handleClick} 
                onActionClickHandler={handleClick} 
                refresh={refreshData} 
              />
            </BorderGradient>
          </div>
          <Modal 
            theme="sdaia" 
            hasPointClouds={false} 
            page="tagTypes"
            selectedTag={tagType} 
            setSelectedTag={setTagType} 
            isLoading={loading} 
            refresh={refreshData} 
            hooks={allHooks} 
            modalConfig={{
              ...modalConfig,
              actionConfig: {
                ...modalConfig.actionConfig,
                tagTypes: {
                  edit: {
                    title: 'Edit Tag Type',
                    formInputs: [
                      { name: 'Name', id: 'name', placeHolder: 'Name', type: "text", isRequired: true, itemWidth: 'full' },
                      { name: 'Description', id: 'description', placeHolder: 'Description', type: "text", isRequired: false, itemWidth: 'full' },
                      { name: 'Match Key', id: 'matchKey', placeHolder: 'Match Key', type: "text", isRequired: false, itemWidth: 'full' },
                      { name: 'Value Key', id: 'valueKey', placeHolder: 'Value Key', type: "text", isRequired: false, itemWidth: 'full' },
                      { name: 'Value Type', id: 'valueType', placeHolder: 'Value Type', type: "text", isRequired: false, itemWidth: 'full' },
                      { name: 'Data Source', id: 'datasourceId', placeHolder: 'Match Value', type: "text", isRequired: false, itemWidth: 'full' },
                    ],
                    hasRanges: false,
                    rangesInputs: [],
                    submitBtn: 'Update',
                    submitAction: updateTagType,
                    isFileUpload: false,
                    hasSelects: false,
                    selects: [],
                  },
                  new: {
                    title: 'New Tag Type',
                    formInputs: [
                      { name: 'Name', id: 'name', placeHolder: 'Name', type: "text", isRequired: true, itemWidth: 'full' },
                      { name: 'Description', id: 'description', placeHolder: 'Description', type: "text", isRequired: false, itemWidth: 'full' },
                      { name: 'Match Key', id: 'matchKey', placeHolder: 'Match Key', type: "text", isRequired: false, itemWidth: 'full' },
                      { name: 'Value Key', id: 'valueKey', placeHolder: 'Value Key', type: "text", isRequired: false, itemWidth: 'full' },
                      { name: 'Value Type', id: 'valueType', placeHolder: 'Value Type', type: "text", isRequired: false, itemWidth: 'full' },
                      { name: 'Data Source', id: 'datasourceId', placeHolder: 'Match Value', type: "text", isRequired: false, itemWidth: 'full' },
                    ],
                    hasRanges: false,
                    rangesInputs: [],
                    submitBtn: 'Create',
                    submitAction: createTagType,
                    isFileUpload: false,
                    hasSelects: false,
                    selects: [],
                  },
                  modalType: 'simple'
                }
              }
            }}
          />
        </div>
      )}
    </>
  );
}
